import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { downloadMediaFile } from "@/components/common/media-preview/downloadMediaFile";
import { MediaPreviewDialog } from "@/components/common/media-preview/MediaPreviewDialog";
import { SingleMediaPreview } from "@/components/common/media-preview/SingleMediaPreview";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import type { MediaFile } from "@/components/common/media-preview/types";
import {
  getMediaDownloadUrl,
  getMediaKind,
  hasFreshSignedMediaUrl,
  TILE_GRID_GAP_CLASS,
  type TileLayoutMode,
} from "@/components/common/media-preview/utils";
import { toast } from "@/hooks/useToast";
import { cn } from "@/lib/utils";
import { triggerBrowserDownload } from "@/lib/utils/mediaFile";

interface MediaPreviewProps {
  files: MediaFile[];
  className?: string;
  layoutVariant?: "content" | "full";
  variant?: "default" | "chat";
  singleTileWidth?: "full" | "fixed" | "fit";
  tileGap?: "default" | "none";
}

const resolveTileLayoutMode = ({
  shouldUseGrid,
  shouldUseTileMode,
  singleTileWidth,
}: {
  shouldUseGrid: boolean;
  shouldUseTileMode: boolean;
  singleTileWidth: NonNullable<MediaPreviewProps["singleTileWidth"]>;
}): TileLayoutMode => {
  if (shouldUseGrid) return "fill-column";
  if (!shouldUseTileMode) return "fit-content";

  if (singleTileWidth === "full") return "fill-column";
  if (singleTileWidth === "fit") return "fit-content";
  return "fixed-width";
};

export const MediaPreview: React.FC<MediaPreviewProps> = ({
  files,
  className = "",
  layoutVariant = "content",
  variant = "default",
  singleTileWidth,
  tileGap,
}) => {
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [showHiddenFiles, setShowHiddenFiles] = useState(false);

  if (!files || files.length === 0) {
    return null;
  }

  const resolvedSingleTileWidth =
    singleTileWidth ?? (variant === "chat" ? "full" : "fixed");
  const resolvedTileGap = tileGap ?? (variant === "chat" ? "none" : "default");
  const visibleFiles = files.slice(0, 3);
  const hiddenFileCount = files.length - visibleFiles.length;

  const shouldUseGrid = files.length >= 2;
  const isSingleNonImageFile =
    files.length === 1 && getMediaKind(files[0]) !== "image";
  const shouldUseTileMode = shouldUseGrid || isSingleNonImageFile;
  const tileLayoutMode = resolveTileLayoutMode({
    shouldUseGrid,
    shouldUseTileMode,
    singleTileWidth: resolvedSingleTileWidth,
  });
  const twoColumnLayoutClass =
    layoutVariant === "full"
      ? "grid-cols-2"
      : "grid-cols-[repeat(2,minmax(0,1fr))]";
  const tileGapClass =
    resolvedTileGap === "none" ? "gap-0" : TILE_GRID_GAP_CLASS;
  const containerClass = shouldUseGrid
    ? `grid ${twoColumnLayoutClass} ${tileGapClass}`
    : isSingleNonImageFile
      ? "grid grid-cols-1 gap-2"
      : "space-y-3";

  const handleOpenPreview = async (index: number) => {
    const file = files[index];
    if (!file) return;

    const mediaKind = getMediaKind(file);
    if (mediaKind === "file") {
      const downloadUrl = getMediaDownloadUrl(file);
      if (downloadUrl && hasFreshSignedMediaUrl(file)) {
        triggerBrowserDownload({
          href: downloadUrl,
          fileName: file.file_name,
        });
        return;
      }

      try {
        await downloadMediaFile({ guid: file.guid, fileName: file.file_name });
      } catch {
        toast({
          title: t("common.error"),
          description: t("common.errorWhileLoading"),
          variant: "error",
        });
      }
      return;
    }

    setSelectedIndex(index);
  };

  return (
    <>
      <div className={cn(containerClass, className)}>
        {visibleFiles.map((file, index) => (
          <div key={file.id} className="relative min-w-0">
            <SingleMediaPreview
              file={file}
              index={index}
              onOpenPreview={handleOpenPreview}
              mode={shouldUseTileMode ? "tile" : "full"}
              layoutMode={tileLayoutMode}
            />
          </div>
        ))}
        {hiddenFileCount > 0 && (
          <button
            type="button"
            className="flex min-h-[72px] min-w-0 items-center justify-center rounded-lg bg-slate-100 text-xl font-semibold text-slate-700 transition-colors hover:bg-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2"
            onClick={() => setShowHiddenFiles(true)}
            aria-label={`${hiddenFileCount} more attachments`}
          >
            +{hiddenFileCount}
          </button>
        )}
      </div>

      <Dialog open={showHiddenFiles} onOpenChange={setShowHiddenFiles}>
        <DialogContent aria-describedby={undefined}>
          <DialogTitle>{t("common.chooseAttachment")}</DialogTitle>
          <ul className="space-y-2">
            {files.slice(visibleFiles.length).map((file, index) => (
              <li key={file.id}>
                <SingleMediaPreview
                  file={file}
                  index={visibleFiles.length + index}
                  onOpenPreview={(fileIndex) => {
                    setShowHiddenFiles(false);
                    void handleOpenPreview(fileIndex);
                  }}
                  mode="tile"
                  layoutMode="fill-column"
                  showFullFileName
                />
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>

      <MediaPreviewDialog
        files={files}
        selectedIndex={selectedIndex}
        onSelectedIndexChange={setSelectedIndex}
      />
    </>
  );
};
